/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { Breadcrumbs, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, {
  MODULE_LAYOUTS,
  getModuleLayoutConfig,
  isHorizontalLayout,
  isImageFirstLayout,
  topImageOnPhone,
} from 'bundles/cds-labs/components/Modules/ModuleLayout';
import Eyebrow from 'bundles/cds-labs/components/Modules/components/Eyebrow';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmButton from 'bundles/cds-labs/components/Modules/components/PreservedUtmButton';
import type { VideoItem } from 'bundles/cds-labs/components/Modules/components/Video';
import Video from 'bundles/cds-labs/components/Modules/components/Video';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';
import {
  HERO_MODULE_TEXT_ALIGNMENT,
  IMAGE_PLACEMENT,
  normalizeImagePlacement,
} from 'bundles/cds-labs/components/Modules/utils/constants';

export type BreadcrumbsItem = {
  label: string;
  href: string;
};

type CallToAction = {
  target: string;
  label: string;
};

type SecondaryCallToAction = CallToAction & {
  variant: Extract<ButtonProps['variant'], 'secondary' | 'ghost'>;
};

type EyebrowType = {
  label?: string | null;
  backgroundColor?: string | null;
  image?: ImageType | null;
  eyebrowImageHeight?: number | null;
};

type ImagePlacementType = (typeof IMAGE_PLACEMENT)[keyof typeof IMAGE_PLACEMENT];

/**
 * These props are available on both the Eng side and Contentful
 */
type HeroCmsProps = {
  cta?: CallToAction | null;
  secondaryCta?: SecondaryCallToAction | null;
  description?: React.ReactNode;
  eyebrow?: EyebrowType | null;
  headline?: React.ReactNode;
  image?: ImageType | null;
  layout?: string | null;
  video?: VideoItem | null;
  imagePlacementOnMobile?: string | null;
  height?: string | null;
  textAlignmentOnMobile?: string | null;
};

/**
 * These props are only available on the Eng side, not Contentful
 */
type HeroEngProps = {
  // default is h1
  headlineComponent?: 'h1' | 'h2';
  imagePlacement?: ImagePlacementType;
  customPrimaryCta?: React.ReactElement;
  customSecondaryCta?: React.ReactElement;
  imageOverlay?: React.ReactNode;
  showImagesAboveContentOnMobile?: boolean | null;
  breadcrumbs?: {
    items: Array<BreadcrumbsItem>;
    onBreadcrumbsItemClick?: (label: string) => void;
  };
};

type HeroContent = HeroCmsProps & HeroEngProps;

export type Props = HeroContent & ModuleLayoutConfig;

const styles = {
  // TODO: the `xs` style is to show a small spacing between the image(top) and the content(bottom) on phone.
  // Ideally, we should add a `gap` to the `Grid`. However, the `PageGridContainer` does not provide API to set the inner `<Grid container>` style,
  // so it's not possible to apply `gap` to the `Grid`.
  // Or less ideally, this spacing should be added to the ModuleLayout so that all modules can benefit from this,
  // but since ModuleLayout has to support many different styles, where many layouts are text-top/image-bottom, and the UI looks not that bad without this spacing.
  // So, in order to limit the scope, we apply this spacing only to Hero and Information (see the same TODO in `InformationModule`.
  content: (addTopPaddingOnMobile: boolean, isTextAlignmentCenter: boolean) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    ${breakpoints.down('xs')} {
      margin-top: ${addTopPaddingOnMobile ? `var(--cds-spacing-100)` : 0};
      align-items: ${isTextAlignmentCenter ? 'center' : 'start'};
    }
  `,
  contentWithBreadcrumbs: css`
    position: relative;
    padding-top: calc(var(--cds-spacing-100));

    ${breakpoints.down('xs')} {
      padding-top: 0;
    }
  `,
  ctaSection: css`
    display: flex;
    flex-direction: row;
    margin-top: var(--cds-spacing-200);
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: center;
      gap: var(--cds-spacing-100);
      width: 100%;

      button {
        max-width: unset;
      }

      button,
      a {
        max-width: unset;

        ${breakpoints.down('xs')} {
          ${typography2.actionSecondary}
          padding: var(--cds-spacing-100) var(--cds-spacing-200);
        }
      }
    }
  `,

  ctaSectionHorizontal: css`
    ${breakpoints.down('sm')} {
      flex-direction: column;

      button {
        width: 100%;
      }
    }
  `,

  ctaSectionCentered: css`
    justify-content: center;

    ${breakpoints.down('sm')} {
      flex-direction: row;
    }

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,

  eyebrowSpacing: (eyebrowImageHeight?: number | null) => css`
    margin-bottom: ${eyebrowImageHeight && eyebrowImageHeight > 32
      ? 'var(--cds-spacing-300)'
      : 'var(--cds-spacing-50)'};
  `,

  noImageCenteredDescription: css`
    width: 75%;
    align-self: center;

    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,

  description: css`
    margin-top: var(--cds-spacing-50);
  `,

  imageWrapperTop: css`
    margin-bottom: 0;
    margin-left: auto;
    margin-right: auto;

    ${breakpoints.down('sm')} {
      margin-bottom: 0;
    }
  `,
  imageWrapperBottom: css`
    margin-top: 0;
    margin-left: auto;
    margin-right: auto;

    ${breakpoints.down('sm')} {
      margin-top: 0;
    }
  `,

  imageWrapperRight: (showImagesAboveContentOnMobile: boolean) => css`
    width: calc(100% + var(--cds-spacing-600));
    margin: calc(var(--cds-spacing-400) * -1);
    margin-right: calc(var(--cds-spacing-600) * -1);
    margin-left: 0;

    ${breakpoints.down('md')} {
      width: calc(100% + var(--cds-spacing-400));
      margin: calc(var(--cds-spacing-300) * -1);
      margin-right: calc(var(--cds-spacing-200) * -1);
      margin-left: 0;
    }

    ${breakpoints.down('xs')} {
      width: calc(100% + var(--cds-spacing-200) * 2);
      ${showImagesAboveContentOnMobile
        ? `margin-top: calc(var(--cds-spacing-300) * -1);
          margin-bottom: 0;`
        : `margin-top: 0;
          margin-bottom: calc(var(--cds-spacing-300) * -1);`}
      margin-left: calc(var(--cds-spacing-200) * -1);
      margin-right: calc(var(--cds-spacing-200) * -1);
    }

    // center image within the container
    img {
      display: block;
      margin: 0 auto;
    }
  `,

  imageWrapperLeft: (showImagesAboveContentOnMobile: boolean) => css`
    width: calc(100% + var(--cds-spacing-600));
    margin: calc(var(--cds-spacing-400) * -1);
    margin-left: calc(var(--cds-spacing-600) * -1);
    margin-right: 0;

    ${breakpoints.down('md')} {
      width: calc(100% + var(--cds-spacing-400));
      margin: calc(var(--cds-spacing-300) * -1);
      margin-left: calc(var(--cds-spacing-200) * -1);
      margin-right: 0;
    }

    ${breakpoints.down('xs')} {
      width: calc(100% + var(--cds-spacing-200) * 2);
      ${showImagesAboveContentOnMobile
        ? `margin-top: calc(var(--cds-spacing-300) * -1);
          margin-bottom: 0;`
        : `margin-top: 0;
          margin-bottom: calc(var(--cds-spacing-300) * -1);`}
      margin-left: calc(var(--cds-spacing-200) * -1);
      margin-right: calc(var(--cds-spacing-200) * -1);
    }

    // center image within the container
    img {
      display: block;
      margin: 0 auto;
    }
  `,
  breadcrumbs: css`
    position: absolute;
    top: 0;
    transform: translateY(-50%);

    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  hideImageOnMobile: css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
};

const imagePlacementStyleMap = {
  [IMAGE_PLACEMENT.TOP]: css`
    && {
      align-items: flex-start;
    }
  `,
};

const HeroModule = (props: Props) => {
  const {
    image,
    video,
    customPrimaryCta,
    imageOverlay,
    showImagesAboveContentOnMobile,
    breadcrumbs,
    customSecondaryCta,
    cta,
    secondaryCta,
    headlineComponent = 'h1',
    imagePlacementOnMobile: contentfulImagePlacementOnMobile,
    textAlignmentOnMobile,
  } = props;
  const track = useTracker();
  const isTextAlignmentCenter = textAlignmentOnMobile === HERO_MODULE_TEXT_ALIGNMENT.CENTER;

  const imagePlacementOnMobile = normalizeImagePlacement(contentfulImagePlacementOnMobile);

  // source of truth for determining if showImagesAboveContentOnMobile is explicitly set to false
  // (or its equivalent in contentful - imagePlacementOnMobile explicitly set to 'bottom') - contentful entry is prioritized.
  const showImageBelowContentOnMobile =
    imagePlacementOnMobile === IMAGE_PLACEMENT.BOTTOM ||
    (!imagePlacementOnMobile && showImagesAboveContentOnMobile === false);

  const isImageFirst = isImageFirstLayout(props);
  const imagePanelStyles = isImageFirst
    ? styles.imageWrapperLeft(!showImageBelowContentOnMobile)
    : styles.imageWrapperRight(!showImageBelowContentOnMobile);

  let verticalImageStyle: SerializedStyles | undefined;
  if (props.layout === MODULE_LAYOUTS.imageTopTextBottom) {
    verticalImageStyle = styles.imageWrapperTop;
  }
  if (props.layout === MODULE_LAYOUTS.textTopImageBottom) {
    verticalImageStyle = styles.imageWrapperBottom;
  }

  const imageOutput = (
    <>
      {image && video && (
        <Video image={image} video={video} preloadImage={props.preloadImage} lazyLoadImage={props.lazyLoadImage} />
      )}
      {image && !video && <Image {...image} shouldPreload={props.preloadImage} shouldLazyLoad={props.lazyLoadImage} />}
    </>
  );

  const imagePanel = () => {
    const shouldHideImageOnMobile = imagePlacementOnMobile === IMAGE_PLACEMENT.NO_IMAGE;

    return (
      <div css={shouldHideImageOnMobile ? styles.hideImageOnMobile : ''}>
        {props.imagePlacement === IMAGE_PLACEMENT.TOP ? (
          imageOutput
        ) : (
          <div css={[imagePanelStyles, verticalImageStyle]}>{imageOutput}</div>
        )}
        {imageOverlay}
      </div>
    );
  };

  const isInverted = isInvertedColor(props.backgroundColor ?? '');

  const primaryCtaVariant = isInverted ? 'primaryInvert' : 'primary';
  let secondaryCtaVariant: ButtonProps['variant'] = 'secondary';
  if (props.secondaryCta?.variant === 'ghost') {
    secondaryCtaVariant = isInverted ? 'ghostInvert' : 'ghost';
  }

  const trackClick = (trackingName: ButtonName, linkTarget?: string, eventingData?: Record<string, string>) => () => {
    track('click_button', {
      button: { name: trackingName, linkURL: linkTarget ?? '', ...eventingData },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const getPrimaryCta = () => {
    if (customPrimaryCta) {
      return customPrimaryCta;
    }

    if (cta) {
      const { target, label } = cta;

      return (
        <PreservedUtmButton
          variant={primaryCtaVariant}
          href={cta.target}
          trackingName="hero_primary_cta"
          onClick={trackClick('hero_primary_cta', target)}
        >
          {label}
        </PreservedUtmButton>
      );
    }

    return null;
  };

  const getSecondaryCta = () => {
    if (customSecondaryCta) {
      return customSecondaryCta;
    }

    if (secondaryCta) {
      const { target, label, variant } = secondaryCta;

      return (
        <PreservedUtmButton
          variant={secondaryCtaVariant}
          href={target}
          icon={variant === 'ghost' ? <ArrowNextIcon /> : undefined}
          trackingName="hero_secondary_cta"
          onClick={trackClick('hero_secondary_cta', target)}
          edgeAlign={variant === 'ghost' ? 'start' : undefined}
        >
          {label}
        </PreservedUtmButton>
      );
    }

    return null;
  };

  const contentPanel = () => (
    <div
      css={[
        styles.content(!showImageBelowContentOnMobile, isTextAlignmentCenter),
        breadcrumbs && styles.contentWithBreadcrumbs,
      ]}
    >
      {breadcrumbs && (
        <Breadcrumbs css={styles.breadcrumbs}>
          {breadcrumbs.items.map(({ label, href }) => (
            <Breadcrumbs.Item key={label} href={href} onClick={() => breadcrumbs.onBreadcrumbsItemClick?.(label)}>
              {label}
            </Breadcrumbs.Item>
          ))}
        </Breadcrumbs>
      )}
      {props.eyebrow && (
        <div css={styles.eyebrowSpacing(props.eyebrow.eyebrowImageHeight)}>
          <Eyebrow
            text={props.eyebrow.label}
            background={props.eyebrow.backgroundColor}
            image={props.eyebrow.image}
            eyebrowImageHeight={props.eyebrow.eyebrowImageHeight}
          />
        </div>
      )}
      {props.headline && (
        <Typography2 variant="titleLargeCompact" component={headlineComponent} color="inherit">
          {props.headline}
        </Typography2>
      )}
      {props.description && (
        <Typography2
          variant="bodyPrimary"
          component="div"
          color="inherit"
          css={[
            styles.description,
            props.layout === MODULE_LAYOUTS.noImageCentered ? styles.noImageCenteredDescription : '',
          ]}
        >
          {props.description}
        </Typography2>
      )}
      {(cta || customPrimaryCta) && (
        <div
          css={[
            styles.ctaSection,
            isHorizontalLayout(props) && styles.ctaSectionHorizontal,
            props.layout === MODULE_LAYOUTS.noImageCentered && styles.ctaSectionCentered,
          ]}
        >
          {getPrimaryCta()}
          {getSecondaryCta()}
        </div>
      )}
    </div>
  );

  // Determine the layout configuration based on both Contentful and engineering props
  const getLayoutConfig = () => {
    const useTopImage = imagePlacementOnMobile
      ? imagePlacementOnMobile !== IMAGE_PLACEMENT.BOTTOM
      : props.showImagesAboveContentOnMobile !== false;
    return getModuleLayoutConfig(useTopImage ? topImageOnPhone(props) : props);
  };

  const assembly = {
    ...getLayoutConfig(),
    ...(props.imagePlacement === IMAGE_PLACEMENT.TOP
      ? {
          customImagePanelStyles: [imagePanelStyles, imagePlacementStyleMap[props.imagePlacement]],
        }
      : {}),
  };

  return <ModuleLayout assembly={assembly} renderContent={contentPanel} renderImage={imagePanel} />;
};

export default HeroModule;
